import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { useProductList } from "../../../../api/productsApi"
import { EntityListRowContent } from "../../../shared/EntityListRowContent"
import { RelBadge } from "../../../shared/RelBadge"
import { EntityPickerTable } from "./EntityPickerTable"

/** Product multi-select picker for the Conformity → Generate flow.
 *
 *  Thin config wrapper around {@link EntityPickerTable} — see that file's
 *  docstring for why the two pickers share an implementation. Row content
 *  follows the Master Data panel: name + product number + articles badge.
 *  The panel's customer name is left out here: a product can map to several
 *  customers and the list carries only one of them.
 */
export function ProductPickerTable({
  selectedIds,
  onToggle,
  hiddenIds,
}: {
  selectedIds: Set<string>
  onToggle: (id: string) => void
  /** Rows to leave out — see EntityPickerTable. */
  hiddenIds?: Set<string>
}) {
  const { translate } = useTranslation()
  return (
    <EntityPickerTable
      selectedIds={selectedIds}
      onToggle={onToggle}
      hiddenIds={hiddenIds}
      useList={useProductList}
      emptyLabelKey="MC_CS_CONF_GEN_PICKER_EMPTY_PRODUCTS"
      entityLabelKey="MC_CS_CONF_GEN_PICKER_ENTITY_PRODUCTS"
      renderContent={(row) => (
        <EntityListRowContent
          title={row.name}
          meta={<span className="font-mono">{row.productNumber}</span>}
          badges={
            <RelBadge
              label={translate("MC_MD_REL_LABEL_ARTICLES")}
              labelOne={translate("MC_MD_REL_LABEL_ARTICLES_ONE")}
              count={row.articlesCount}
              tone="muted"
              items={row.relArticles.map((a) => ({
                id: a.id,
                primary: a.articleNumber,
                secondary: a.name,
              }))}
            />
          }
        />
      )}
    />
  )
}
